import {
  Badge,
  BadgeColor,
  BadgeContainer,
} from '@delmonicos/shared-design-system-react/src/components/badge/badge';
import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Dialog,
  DialogPosition,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import {
  ResourceStatus,
  withProtectedResource,
} from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { GatewayGetTransactionsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/transactions/models/inputs/gateway-get-transactions-paginated.model';
import {
  hasAccessTo,
  safeHasAccessTo,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { transformPaginatedResponse } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transform-api-response';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import {
  OnChangeFn,
  SortingState,
  functionalUpdate,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { permissions } from '@app/core/authentication/authentication.slice';
import { getState } from '@app/core/helper/get-state';
import { useAppSelector } from '@app/core/hooks';
import { useCpmsBoGetTransactionsPaginatedQuery } from '@service/cpms-bo-transaction.service';
import { useSetPageActionContext } from '@shared/layout/page-layout/page-layout-action.context-provider';
import {
  createColumnOrderChangeHandler,
  createHideColumnHandler,
} from '@shared/list-options/column-options.helper';

import { TransactionListExportController } from '../transaction-list-export/transaction-list-export.controller';
import { TransactionListFilterSearchController } from '../transaction-list-filter-search/transaction-list-filter-search.controller';
import { TransactionListFilterStateController } from '../transaction-list-filter-state/transaction-list-filter-state.controller';
import { TransactionListFilterController } from '../transaction-list-filter/transaction-list-filter.controller';
import { TransactionListOptionsController } from '../transaction-list-options/transaction-list-options.controller';
import {
  TransactionListContextProvider,
  useTransactionListContext,
} from '../transaction.context';
import {
  TRANSACTION_LIST_FILTER_NAME_LIST,
  TRANSACTION_LIST_SORT_FIELD,
} from './transaction-list.config';
import * as css from './transaction-list.module.scss';
import { TransactionListView } from './transaction-list.view';

export const TransactionListController = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const {
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    getPaginatedLimitList,
    columnOptions,
    setColumnOptions,
  } = useTransactionListContext();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionExportIsDialogOpen, setActionExportIsDialogOpen] =
    useState(false);

  const [actionFilterIsDialogOpen, setActionFilterIsDialogOpen] =
    useState(false);

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const queryState = useCpmsBoGetTransactionsPaginatedQuery(
    getPaginatedQueryParameters,
  );

  const paginationState = useMemo(
    () =>
      getState({
        isLoading: queryState.isLoading,
        isError: queryState.isError,
        isFetching: queryState.isFetching,
      }),
    [queryState.isLoading, queryState.isError, queryState.isFetching],
  );

  // TODO: Should be moved in filter-search component
  //   if (debouncedTextToSearch && debouncedTextToSearch.trim().length > 0) {
  //     params.textToSearch = debouncedTextToSearch.trim() || '';
  //   }

  // TODO: Should be move in dedicated component
  const filterNameActiveList = TRANSACTION_LIST_FILTER_NAME_LIST.filter(
    (filterName) => getPaginatedQueryParameters[filterName] !== undefined,
  );

  const filerActiveHiddenCount = Math.max(
    0,
    filterNameActiveList.reduce((count, filterName) => {
      const filterValue = getPaginatedQueryParameters[filterName] as any;

      if (Array.isArray(filterValue)) {
        return count + filterValue.length;
      }

      return count + 1;
    }, 0) - 3,
  );

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  const setPageAction = useSetPageActionContext();

  useEffect(() => {
    const actions: React.JSX.Element[] = [];

    if (
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.TRANSACTION_REPORTS,
        action: ActionsByResource.TRANSACTION_REPORTS.READ,
      })
    ) {
      actions.push(
        <ButtonIcon
          icon="download-01"
          onClick={() => setActionExportIsDialogOpen(true)}
        />,
      );
    }

    setPageAction(actions);
  }, [setPageAction, userPermissions]);

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handlePaginationPageChange = (paginationPageNext: number) => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      offset: (paginationPageNext - 1) * current.limit,
    }));
  };

  const handlePaginationLimitChange = (paginationLimitNext: number) => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      limit: paginationLimitNext,
      offset: 0,
    }));
  };

  const handlePaginationSortingChange: OnChangeFn<SortingState> = (
    sortingState,
  ) => {
    setGetPaginatedQueryParameters((current) => {
      const paginationSorting0 = functionalUpdate(sortingState, [
        {
          desc: current.sortOrder === -1,
          id: current.sortField ?? TRANSACTION_LIST_SORT_FIELD,
        },
      ]).at(0);

      return {
        ...current,
        sortField:
          paginationSorting0?.id as GatewayGetTransactionsPaginatedModel['sortField'],
        sortOrder: paginationSorting0?.desc ? -1 : 1,
      };
    });
  };

  const handleHideColumn = createHideColumnHandler(setColumnOptions);
  const handleColumnOrderChange =
    createColumnOrderChangeHandler(setColumnOptions);

  // RENDER ////////////////////////////////////////////////////////////////////

  const { Component: TransactionListViewProtected, status: resourceStatus } =
    withProtectedResource(TransactionListView, queryState, {
      isAuthorized: () =>
        safeHasAccessTo(userPermissions, {
          resourceName: Resource.TRANSACTIONS,
          action: ActionsByResource.TRANSACTIONS.READ,
        }),
      transformDataToResource: transformPaginatedResponse,
    })({
      paginationLimit: getPaginatedQueryParameters.limit,
      paginationLimitList: getPaginatedLimitList,
      paginationState,
      paginationSorting: [
        {
          desc: getPaginatedQueryParameters.sortOrder === -1,
          id:
            getPaginatedQueryParameters.sortField ??
            TRANSACTION_LIST_SORT_FIELD,
        },
      ],
      columnVisibility: columnOptions.columnVisibility,
      columnOrder: columnOptions.columnOrder,
      renderColumnSettings: () => <TransactionListOptionsController />,
      onPaginationLimitChange: handlePaginationLimitChange,
      onPaginationPageChange: handlePaginationPageChange,
      onPaginationSortingChange: handlePaginationSortingChange,
      onHideColumn: handleHideColumn,
      onColumnOrderChange: handleColumnOrderChange,
    });

  return (
    <Stack flex={1} className={css.listContainer} direction="column">
      {resourceStatus === ResourceStatus.SUCCESS && (
        <Stack direction="row" justifyContent="space-between" gap={GapEnum.XL}>
          <Stack flex={1}>
            <TransactionListFilterSearchController
              onCancel={() => {}}
              onSuccess={() => {}}
            />
          </Stack>
          <TransactionListFilterStateController />
          <Stack justifyContent="end" gap={GapEnum.M}>
            <BadgeContainer
              badge={
                filerActiveHiddenCount > 0 && (
                  <Badge color={BadgeColor.Secondary}>
                    {`+${filerActiveHiddenCount}`}
                  </Badge>
                )
              }>
              <ButtonIcon
                icon="filter-funnel-01"
                onClick={() => setActionFilterIsDialogOpen(true)}
              />
            </BadgeContainer>
          </Stack>
        </Stack>
      )}
      <Stack className={css.tableContainer}>
        {TransactionListViewProtected}
      </Stack>
      {resourceStatus === ResourceStatus.SUCCESS && (
        <>
          <Dialog
            open={actionExportIsDialogOpen}
            onClose={() => setActionExportIsDialogOpen(false)}
            position={DialogPosition.RIGHT}>
            <Dialog.Header
              icon="download-01"
              onClose={() => setActionExportIsDialogOpen(false)}
              title={t('$.transaction-list-export.title')}
            />
            <Dialog.Content>
              <TransactionListExportController
                onCancel={() => setActionExportIsDialogOpen(false)}
                onSuccess={() => setActionExportIsDialogOpen(false)}
              />
            </Dialog.Content>
          </Dialog>
          <Dialog
            open={actionFilterIsDialogOpen}
            onClose={() => setActionFilterIsDialogOpen(false)}
            position={DialogPosition.RIGHT}>
            <Dialog.Header
              icon="filter-funnel-01"
              onClose={() => setActionFilterIsDialogOpen(false)}
              title={t('$.transaction-list-filter.title')}
            />
            <Dialog.Content>
              <TransactionListFilterController
                onCancel={() => setActionFilterIsDialogOpen(false)}
                onSuccess={() => setActionFilterIsDialogOpen(false)}
              />
            </Dialog.Content>
          </Dialog>
        </>
      )}
    </Stack>
  );
};

export const TransactionListControllerWithContext = (): React.JSX.Element => {
  return (
    <TransactionListContextProvider>
      <TransactionListController />
    </TransactionListContextProvider>
  );
};
